import CircularLoader, {
  CircularLoaderType,
} from '@allesauto/core/circularLoader';
import { type Option } from '@allesauto/core/searchDropdown';
import StepIndicator, { StepItem } from '@allesauto/core/StepIndicator';
import { useWhiteLabel } from '@allesauto/core/whiteLabel';
import { SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import AccessoryDeliveryOptions from '@/components/AccessoryDeliveryOptions/AccessoryDeliveryOptions';
import DealerFinder from '@/components/DealerFinder/DealerFinder';
import Footer from '@/components/OverlayFormFooter/OverlayFormFooter';

import { useAccessoryPriceDetails } from '@/features/Accessories/hooks';

export interface AccessoryDealerPriceOverlayProps {
  accessory: SchemaTypes.Accessory;
  searchLocation?: Option | null | undefined;
  dealer?: SchemaTypes.Dealer | undefined;
  shipment?: SchemaTypes.Shipment;
  step?: number;
  onSubmit: (
    searchLocation: Option | null | undefined,
    dealer: SchemaTypes.Dealer,
    dealerPrice: number,
    dealerProductId: string,
    shipment: SchemaTypes.Shipment
  ) => void;
  garageOverlay?: boolean;
}

export const STEPS = {
  DEALER: 0,
  DELIVERY: 1,
};

const AccessoryDealerPriceOverlay = ({
  accessory,
  searchLocation,
  dealer,
  shipment,
  step = STEPS.DEALER,
  onSubmit,
  garageOverlay = false,
}: AccessoryDealerPriceOverlayProps) => {
  const intl = useIntl();

  const whiteLabel = useWhiteLabel();
  const [currentStep, setCurrentStep] = useState(step);
  const [selectedDealer, setSelectedDealer] = useState<
    SchemaTypes.Dealer | undefined
  >(dealer);
  const [dealerSearchLocation, setDealerSearchLocation] = useState<
    Option | null | undefined
  >(searchLocation); // is used only to restore the state of DealerFinder
  const [selectedShipment, setSelectedShipment] = useState<
    SchemaTypes.Shipment | undefined
  >(shipment);

  const {
    executeCall,
    data: priceDetails,
    loading: priceDetailsLoading,
  } = useAccessoryPriceDetails();

  useEffect(() => {
    if (garageOverlay) {
      executeCall(accessory.dealerId, accessory.productId);
    } else {
      executeCall(
        selectedDealer?.accessoryDealerId ?? undefined,
        accessory.productId
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDealer, accessory.productId]);

  useEffect(() => {
    if (
      priceDetails &&
      !selectedShipment &&
      priceDetails.shippingOptions &&
      priceDetails.shippingOptions.length
    ) {
      setSelectedShipment(priceDetails.shippingOptions[0]); // select the 1st shipping options as a default one
    }
  }, [priceDetails, selectedShipment]);

  const onConfirm = useCallback(() => {
    if (currentStep === STEPS.DEALER) {
      if (!selectedDealer) {
        return;
      }

      setCurrentStep(STEPS.DELIVERY);
    } else {
      if (!selectedDealer || !priceDetails || !selectedShipment) {
        return;
      }

      onSubmit(
        dealerSearchLocation,
        selectedDealer,
        priceDetails.price,
        priceDetails.productId, // the id that belongs to the selected dealer and that should be added to the cart then
        selectedShipment
      );
    }
  }, [
    currentStep,
    selectedDealer,
    priceDetails,
    selectedShipment,
    onSubmit,
    dealerSearchLocation,
  ]);

  const className = {
    wrapper: 'pb-[140px]', // 140px bottom padding is to add space for the footer
    dealerFinderWrapper: 'mt-20px md:mt-30px',
    dealersDescr1: 'font-regular font-weight-bold mb-10px text-black',
    dealersDescr2: 'font-regular mb-30px text-grey60',
    deliveryWrapper: 'mt-30px md:mt-40px',
    deliveryLoader:
      'flex h-full items-center justify-center mt-60px md:mt-100px',
    product: 'mb-30px md:mb-40px',
  };

  return (
    <>
      <StepIndicator currentStep={currentStep}>
        <StepItem
          label={translate(
            intl,
            'ui.PDP_ACCESSORIES.DEALER_PRICE_OVERLAY.DEALER_STEP'
          )}
          onClick={() => setCurrentStep(STEPS.DEALER)}
        />
        <StepItem
          label={translate(
            intl,
            'ui.PDP_ACCESSORIES.DEALER_PRICE_OVERLAY.DELIVERY_STEP'
          )}
        />
      </StepIndicator>

      <div className={className.wrapper}>
        {currentStep === STEPS.DEALER && (
          <div className={className.dealerFinderWrapper}>
            <p className={className.dealersDescr1}>
              {translate(
                intl,
                whiteLabel
                  ? 'ui.PDP_ACCESSORIES.DEALER_PRICE_OVERLAY.DEALER_DESCR_1_WL'
                  : 'ui.PDP_ACCESSORIES.DEALER_PRICE_OVERLAY.DEALER_DESCR_1'
              )}
            </p>
            <p className={className.dealersDescr2}>
              {translate(
                intl,
                whiteLabel
                  ? 'ui.PDP_ACCESSORIES.DEALER_PRICE_OVERLAY.DEALER_DESCR_2_WL'
                  : 'ui.PDP_ACCESSORIES.DEALER_PRICE_OVERLAY.DEALER_DESCR_2',
                {
                  accessory: accessory.title,
                }
              )}
            </p>

            <DealerFinder
              onDealerSelect={(dealer) => setSelectedDealer(dealer)}
              beforeDealerSearch={({ option }) =>
                setDealerSearchLocation(option)
              }
              selectedSearchOption={dealerSearchLocation}
              selectedDealerId={selectedDealer?.id}
              optionalFilterParameters={{
                accessory: {
                  distributorDealerId: accessory.dealerId,
                  productId: accessory.productId,
                },
              }}
              placeholder={translate(
                intl,
                whiteLabel
                  ? 'ui.PDP_ACCESSORIES.DEALER_PRICE_OVERLAY.DEALER_SEARCH_INPUT_PLACEHOLDER_WL'
                  : 'ui.PDP_ACCESSORIES.DEALER_PRICE_OVERLAY.DEALER_SEARCH_INPUT_PLACEHOLDER'
              )}
            />
          </div>
        )}

        {currentStep === STEPS.DELIVERY && selectedDealer && (
          <div className={className.deliveryWrapper}>
            {priceDetailsLoading && (
              <div className={className.deliveryLoader}>
                <CircularLoader type={CircularLoaderType.secondary} />
              </div>
            )}
            {priceDetails && (
              <AccessoryDeliveryOptions
                accessory={accessory}
                dealer={selectedDealer}
                priceDetails={priceDetails}
                selectedShipment={selectedShipment}
                onShipmentChange={setSelectedShipment}
              />
            )}
          </div>
        )}
      </div>

      <Footer
        buttonText={translate(
          intl,
          `ui.PDP_ACCESSORIES.DEALER_PRICE_OVERLAY.${
            currentStep === STEPS.DEALER ? 'BUTTON_NEXT' : 'BUTTON_TO_CART'
          }`
        )}
        isFromAccessory={true}
        onButtonClick={onConfirm}
      />
    </>
  );
};

export default AccessoryDealerPriceOverlay;
